import { DialogContentText } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";

interface InactivityDialogProps {
  open: boolean;
  /** Seconds until the session expires, mirrored from the server. */
  remaining: number;
  /**
   * False when the session has hit its absolute lifetime cap. Extending then buys nothing, so we
   * must not offer a "Stay signed in" button that cannot work — we tell the user to re-authenticate.
   */
  canExtend: boolean;
  handleStillHere: () => void;
  handleLogoutNow: () => void;
}

function formatRemaining(totalSeconds: number): string {
  const s = Math.max(0, totalSeconds);
  if (s < 60) return `${s} ${s === 1 ? "second" : "seconds"}`;
  const minutes = Math.floor(s / 60);
  const seconds = s % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function InactivityDialog(props: InactivityDialogProps) {
  const { open, remaining, canExtend, handleStillHere, handleLogoutNow } = props;

  return (
    <Dialog open={open} aria-labelledby="session-expiry-title">
      <DialogTitle id="session-expiry-title">
        {canExtend ? "Session Expiration Warning" : "Session Ending"}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText id="session-expiry-description">
          {canExtend ? (
            <>
              Your session is about to expire due to inactivity.
              <br />
              <br />
              Time remaining: {formatRemaining(remaining)}
            </>
          ) : (
            <>
              Your session has reached its maximum length and will end. For security, you&apos;ll
              need to sign in again to continue.
              <br />
              <br />
              Time remaining: {formatRemaining(remaining)}
            </>
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        {canExtend && (
          <Button onClick={handleStillHere} variant="contained" color="primary">
            Stay Signed In
          </Button>
        )}
        <Button
          onClick={handleLogoutNow}
          variant={canExtend ? "outlined" : "contained"}
          color="primary"
        >
          {canExtend ? "Log Out Now" : "Sign In Again"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
